import { API_ENDPOINTS } from "@api/ApiEndpoints";
import { convertStatusToNumberRecommendation, RecommendationStatus } from "@cloudclevr/components";
import { RecommendationDto } from "./types";
import { useHttpClient } from "@hooks/utils/httpConfig";
import { useMutation, useQuery, UseQueryResult } from "@tanstack/react-query";

export function useCategoryRecommendationsQuery(
  categoryId?: string,
  status?: RecommendationStatus,
  navigationProperties?: string[],
  practice?: number[]
): UseQueryResult<RecommendationDto[]> {
  const client = useHttpClient();

  const fetchRecommendations = async (): Promise<RecommendationDto[]> => {

    try {    
      if (!categoryId) {
        throw new Error('Category ID is required');
      }
      
      const propsList = navigationProperties?.join(',');
      
      // FIX: Convert the status enum to numeric value that the API expects
      const statusNumber = status ? convertStatusToNumberRecommendation(status) : undefined;      
      const statusString = statusNumber !== undefined ? statusNumber.toString() : undefined;      

      const response = await client.get<RecommendationDto[]>(
        API_ENDPOINTS.CATEGORY_RECOMMENDATIONS(categoryId, propsList, statusString, practice));            
      
      // Sort by severity ascending, then by subcategory name ascending
      return response.sort((a, b) => {
          const severityCompare = (a.severity ?? 999) - (b.severity ?? 999);
          if (severityCompare !== 0) return severityCompare;
          const subCatCompare = (a.subCategory?.name ?? '').localeCompare(b.subCategory?.name ?? '', undefined, { sensitivity: 'base' });
          if (subCatCompare !== 0) return subCatCompare;
          // If both severity and subcategory name are equal, sort by title (case-insensitive)
          return (a.title ?? '').localeCompare(b.title ?? '', undefined, { sensitivity: 'base' });
      });
    }
    catch (error) {
      console.error('Error fetching category recommendations:', error);
      throw error;
    }
  };

  return useQuery({ queryKey: ['category-recommendations', categoryId, status, navigationProperties, practice], queryFn: fetchRecommendations, 
      enabled: !!categoryId,
      refetchOnWindowFocus: false,
      gcTime: 10 * 60 * 1000, // 10 minutes (renamed from cacheTime in V5)
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );
}
// Hook to get download token for attachment
export function useGetAttachmentDownloadToken() {
  const client = useHttpClient();

  return useMutation<string, Error, {
    roadmapId: string;
    recommendationId: string;
    attachmentId: string;
  }>({
    mutationFn: async ({ roadmapId, recommendationId, attachmentId }) => {
      const response = await client.get<string>(
        API_ENDPOINTS.ATTACHMENT_DOWNLOAD_TOKEN(roadmapId, recommendationId, attachmentId)
      );
      
      return response;
    }
  });
}